const TAB_ID_STORAGE_KEY = 'native-audience-persistence-tab-id:v1';
const TAB_ID_LOCK_PREFIX = 'native-audience-persistence-tab:v1:';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type TabIdStorage = Pick<Storage, 'getItem' | 'setItem'>;

type HeldLock = { name: string };

export type NativeAudienceLockManager = {
  request: (
    name: string,
    options: { mode: 'exclusive'; ifAvailable: true },
    callback: (lock: HeldLock | null) => Promise<void> | void,
  ) => Promise<void>;
};

export type NativeAudienceTabLeaseEnvironment = {
  locks: NativeAudienceLockManager | null;
  storage: TabIdStorage | null;
  createId: () => string;
};

function storedTabId(storage: TabIdStorage | null) {
  if (!storage) return null;
  try {
    const value = storage.getItem(TAB_ID_STORAGE_KEY);
    return value && UUID_PATTERN.test(value) ? value : null;
  } catch {
    return null;
  }
}

function persistTabId(storage: TabIdStorage | null, tabId: string) {
  if (!storage) return;
  try {
    storage.setItem(TAB_ID_STORAGE_KEY, tabId);
  } catch {
    // A held lock still makes this document's identity safe. Persistence is an
    // optional reload convenience when browser privacy settings allow it.
  }
}

function holdLockForDocumentLifetime(
  locks: NativeAudienceLockManager,
  tabId: string,
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    try {
      void locks.request(
        `${TAB_ID_LOCK_PREFIX}${tabId}`,
        { mode: 'exclusive', ifAvailable: true },
        async (lock) => {
          if (!lock) {
            resolve(false);
            return;
          }

          resolve(true);
          // Do not release on route unmount or pagehide. A page in the back/
          // forward cache is still a live document and must retain ownership.
          // For a real reload or navigation, the browser releases Web Locks
          // when it destroys the old document.
          await new Promise<void>(() => undefined);
        },
      ).catch(reject);
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Claims a tab id before callers can use it to address persisted work.
 *
 * The dependency argument exists for deterministic unit tests. Production
 * callers use getOrCreateNativeAudienceTabId(), whose promise is shared for the
 * entire document lifetime.
 */
export async function acquireNativeAudienceTabId({
  locks,
  storage,
  createId,
}: NativeAudienceTabLeaseEnvironment) {
  // Always create a fresh fallback before reading sessionStorage. If the Locks
  // API is missing or unusable, adopting the stored id could make a duplicated
  // tab restore another live document's draft or pending request.
  const documentFallback = createId();
  if (!locks) return documentFallback;

  try {
    const existing = storedTabId(storage);
    if (existing && await holdLockForDocumentLifetime(locks, existing)) return existing;

    // A duplicated tab initially has a clone of its opener's sessionStorage.
    // Rotate until this document holds the corresponding exclusive lock, then
    // replace only this browsing context's stored id.
    let candidate = documentFallback;
    for (let attempt = 0; attempt < 8; attempt += 1) {
      if (await holdLockForDocumentLifetime(locks, candidate)) {
        persistTabId(storage, candidate);
        return candidate;
      }
      candidate = createId();
    }
  } catch {
    // Treat a throwing or policy-disabled Locks API like an unsupported one:
    // use a document-only id and do not adopt or persist cloned ownership.
    return documentFallback;
  }

  throw new Error('Unable to acquire an exclusive native audience tab identity');
}

function browserEnvironment(): NativeAudienceTabLeaseEnvironment {
  const locks: NativeAudienceLockManager | null = typeof navigator !== 'undefined' && navigator.locks
    ? {
        request: (name, options, callback) => navigator.locks.request(
          name,
          options,
          callback,
        ).then(() => undefined),
      }
    : null;

  return {
    locks,
    storage: typeof sessionStorage === 'undefined' ? null : sessionStorage,
    createId: () => {
      if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    },
  };
}

let documentTabId: Promise<string> | null = null;

export function getOrCreateNativeAudienceTabId() {
  if (!documentTabId) {
    documentTabId = acquireNativeAudienceTabId(browserEnvironment());
  }
  return documentTabId;
}
